import React, { useRef, useEffect, useState } from 'react';
import EditorJS from '@editorjs/editorjs';
import Header from '@editorjs/header';
import List from '@editorjs/list';
import Paragraph from '@editorjs/paragraph';
import Quote from '@editorjs/quote';
import Table from '@editorjs/table';
import Delimiter from '@editorjs/delimiter';
import CodeTool from '@editorjs/code';
import LinkTool from '@editorjs/link';
import Embed from '@editorjs/embed';
import Checklist from '@editorjs/checklist';
import Marker from '@editorjs/marker';
import InlineCode from '@editorjs/inline-code';
import ImageTool from '@editorjs/image';
import axios from 'axios';
import './editorjs-styles.css';

/**
 * Editor.js - FREE Open Source Version
 * 
 * License: Apache 2.0 (completely free!)
 * - No API key required
 * - No usage limits
 * - No branding or attribution required
 * - Block-based editing with JSON output
 * - Perfect for commercial use
 * 
 * Docs: https://editorjs.io/
 */

function EditorJSComponent({ initialContent, onContentChange, title }) {
  const editorInstance = useRef(null);
  const [isReady, setIsReady] = useState(false);

  useEffect(() => {
    if (!editorInstance.current) {
      initEditor();
    }

    return () => {
      if (editorInstance.current && editorInstance.current.destroy) {
        editorInstance.current.destroy();
        editorInstance.current = null;
      }
    };
  }, []);

  const initEditor = () => {
    const editor = new EditorJS({
      holder: 'editorjs',
      tools: {
        header: {
          class: Header,
          config: {
            placeholder: 'Enter a header',
            levels: [1, 2, 3, 4, 5, 6],
            defaultLevel: 2
          }
        },
        paragraph: {
          class: Paragraph,
          inlineToolbar: true
        },
        list: {
          class: List,
          inlineToolbar: true,
          config: {
            defaultStyle: 'unordered'
          }
        },
        checklist: {
          class: Checklist,
          inlineToolbar: true
        },
        quote: {
          class: Quote,
          inlineToolbar: true,
          config: {
            quotePlaceholder: 'Enter a quote',
            captionPlaceholder: 'Quote\'s author'
          }
        },
        marker: {
          class: Marker,
          shortcut: 'CMD+SHIFT+M'
        },
        code: {
          class: CodeTool,
          shortcut: 'CMD+SHIFT+C'
        },
        delimiter: Delimiter,
        inlineCode: {
          class: InlineCode,
          shortcut: 'CMD+SHIFT+K'
        },
        linkTool: {
          class: LinkTool,
          config: {
            endpoint: '/api/content/fetch-link-meta'
          }
        },
        image: {
          class: ImageTool,
          config: {
            uploader: {
              uploadByFile(file) {
                const formData = new FormData();
                formData.append('file', file);

                return axios.post('/api/content/upload-image', formData, {
                  headers: {
                    'Content-Type': 'multipart/form-data',
                    'Authorization': `Bearer ${localStorage.getItem('token')}`
                  }
                }).then(response => {
                  return {
                    success: 1,
                    file: {
                      url: response.data.location
                    }
                  };
                }).catch(error => {
                  console.error('Upload failed:', error);
                  return {
                    success: 0
                  };
                });
              }
            }
          }
        },
        embed: {
          class: Embed,
          config: {
            services: {
              youtube: true,
              vimeo: true,
              twitter: true,
              instagram: true,
              facebook: true
            }
          }
        },
        table: {
          class: Table,
          inlineToolbar: true,
          config: {
            rows: 2,
            cols: 3
          }
        }
      },
      data: parseInitialContent(initialContent),
      onChange: async () => {
        if (editorInstance.current) {
          const outputData = await editorInstance.current.save();
          const html = convertEditorJSToHTML(outputData);
          if (onContentChange) {
            onContentChange(html);
          }
        }
      },
      onReady: () => {
        setIsReady(true);
      }
    });

    editorInstance.current = editor;
  };

  const parseInitialContent = (htmlContent) => {
    if (!htmlContent) {
      return {
        blocks: [
          {
            type: 'paragraph',
            data: {
              text: 'Start editing...'
            }
          }
        ]
      };
    }

    // Simple HTML to EditorJS conversion
    // For a production app, you'd want a more robust parser
    const blocks = [];
    const parser = new DOMParser();
    const doc = parser.parseFromString(htmlContent, 'text/html');
    const elements = doc.body.children;

    for (let element of elements) {
      const tagName = element.tagName.toLowerCase();
      
      if (tagName.match(/^h[1-6]$/)) {
        blocks.push({
          type: 'header',
          data: {
            text: element.textContent,
            level: parseInt(tagName[1])
          }
        });
      } else if (tagName === 'p') {
        blocks.push({
          type: 'paragraph',
          data: {
            text: element.innerHTML
          }
        });
      } else if (tagName === 'ul' || tagName === 'ol') {
        const items = Array.from(element.querySelectorAll('li')).map(li => li.textContent);
        blocks.push({
          type: 'list',
          data: {
            style: tagName === 'ul' ? 'unordered' : 'ordered',
            items: items
          }
        });
      } else if (tagName === 'img') {
        blocks.push({
          type: 'image',
          data: {
            file: {
              url: element.src
            },
            caption: element.alt || '',
            withBorder: false,
            stretched: false,
            withBackground: false
          }
        });
      } else if (tagName === 'blockquote') {
        blocks.push({
          type: 'quote',
          data: {
            text: element.textContent,
            caption: '',
            alignment: 'left'
          }
        });
      }
    }

    return blocks.length > 0 ? { blocks } : {
      blocks: [
        {
          type: 'paragraph',
          data: {
            text: htmlContent
          }
        }
      ]
    };
  };

  const convertEditorJSToHTML = (data) => {
    let html = '';

    data.blocks.forEach(block => {
      switch (block.type) {
        case 'header':
          html += `<h${block.data.level}>${block.data.text}</h${block.data.level}>`;
          break;
        case 'paragraph':
          html += `<p>${block.data.text}</p>`;
          break;
        case 'list':
          const tag = block.data.style === 'ordered' ? 'ol' : 'ul';
          html += `<${tag}>`;
          block.data.items.forEach(item => {
            html += `<li>${item}</li>`;
          });
          html += `</${tag}>`;
          break;
        case 'image':
          html += `<img src="${block.data.file.url}" alt="${block.data.caption || ''}" style="max-width: 100%; height: auto;" />`;
          if (block.data.caption) {
            html += `<p><em>${block.data.caption}</em></p>`;
          }
          break;
        case 'quote':
          html += `<blockquote>${block.data.text}`;
          if (block.data.caption) {
            html += `<footer>${block.data.caption}</footer>`;
          }
          html += `</blockquote>`;
          break;
        case 'code':
          html += `<pre><code>${block.data.code}</code></pre>`;
          break;
        case 'delimiter':
          html += `<hr />`;
          break;
        case 'table':
          html += `<table border="1" style="border-collapse: collapse;">`;
          block.data.content.forEach(row => {
            html += `<tr>`;
            row.forEach(cell => {
              html += `<td style="padding: 8px;">${cell}</td>`;
            });
            html += `</tr>`;
          });
          html += `</table>`;
          break;
        case 'checklist':
          html += `<ul style="list-style: none;">`;
          block.data.items.forEach(item => {
            html += `<li><input type="checkbox" ${item.checked ? 'checked' : ''} disabled /> ${item.text}</li>`;
          });
          html += `</ul>`;
          break;
        default:
          if (block.data.text) {
            html += `<p>${block.data.text}</p>`;
          }
      }
    });

    return html;
  };

  return (
    <div style={{ background: 'white', padding: '20px', borderRadius: '4px', minHeight: '500px' }}>
      <div id="editorjs"></div>
    </div>
  );
}

export default EditorJSComponent;
